import { Fragment, useEffect, useMemo, useState } from 'react';
import { Row, Col, Table as BTable, Pagination, Form } from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useAtom } from 'jotai';
import { pageSizeRecoil } from './recoil';
import { usePortalContext } from '..//lib/context/portalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp, faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';

// Resolves a column's dataField against a row, supporting dot-notation
// nested paths (e.g. 'customer_admin.id') the same way the TanStack
// accessorFn branch below does. Plain bracket access (row[dataField])
// breaks for nested fields since there's no literal 'customer_admin.id'
// key on the row object — it'd just return undefined for every row.
const resolveField = (row, dataField) =>
  dataField?.includes('.')
    ? dataField.split('.').reduce((obj, key) => obj?.[key], row)
    : row?.[dataField];

const Table = ({
  data,
  columns,
  loading,
  pageNum,
  setPageNum,
  totalRecords,
  setSort,
  disableRowClick,
  testId,
}) => {
  const { togglePortal, isShowing } = usePortalContext() || {};
  const [tablePageSize, setTablePageSize] = useAtom(pageSizeRecoil);
  const [selectedId, setSelectedId] = useState(null);

  // Tracks sort state ourselves since manualSorting:true means TanStack
  // Table won't manage it internally — without this, col.getIsSorted()
  // always reads as unsorted and every click recomputes 'asc' instead
  // of toggling asc/desc on repeated clicks of the same column.
  const [sortState, setSortState] = useState({ field: null, order: null });

  // ── Derived pagination values (mirrors the original firstOnPage/lastOnPage logic)
  const firstOnPage = (pageNum - 1) * tablePageSize + 1;
  const lastOnPage  = Math.min(pageNum * tablePageSize, totalRecords);
  const pageCount   = Math.ceil(totalRecords / tablePageSize);

  // In Table component — only decrement if we're sure the page is genuinely empty
  useEffect(() => {
    if (!loading && data.length === 0 && pageNum > 1) {
      const timer = setTimeout(() => setPageNum(prev => prev - 1), 0);
      return () => clearTimeout(timer);
    }
  }, [data.length, loading, pageNum]);

  useEffect(() => {
    if (!isShowing) setSelectedId(null);
  }, [isShowing]);

  // ── TanStack column defs — convert from react-bootstrap-table-next shape
  // The old shape:  { dataField, text, sort, formatter, headerStyle, ... }
  // The new shape:  { accessorKey, header, cell, enableSorting, ... }
  const tanstackColumns = useMemo(
    () =>
      columns.map((col) => {
        const isNested = col.dataField?.includes('.');

        return {
          // Use accessor function for nested paths, accessorKey for flat
          ...(isNested
            ? {
                id: col.dataField,
                accessorFn: (row) => col.dataField.split('.').reduce(
                  (obj, key) => obj?.[key],
                  row
                ),
              }
            : {
                accessorKey: col.dataField,
                id: col.dataField,
              }
          ),
          header: col.text ?? '',
          enableSorting: col.sort ?? false,
          enableHiding: true,
          // Preserve any custom cell renderer the caller passed as `formatter`.
          // Old signature: formatter(cell, row, rowIndex)
          // New signature: cell(info) — we adapt it here so call sites don't change.
          ...(col.formatter && {
            cell: (info) =>
              col.formatter(info.getValue(), info.row.original, info.row.index),
          }),
          ...(col.headerStyle && { meta: { headerStyle: col.headerStyle } }),
        };
      }),
    [columns]
  );

  const initialColumnVisibility = useMemo(
    () =>
      columns.reduce((acc, col) => {
        if (col.hidden) acc[col.dataField] = false;
        return acc;
      }, {}),
    [columns]
  );

  const table = useReactTable({
    data,
    columns:           tanstackColumns,
    getCoreRowModel:   getCoreRowModel(),
    manualPagination:  true,
    manualSorting:     true,
    autoResetPageIndex: false,
    rowCount:          totalRecords,
    state: {
      // Controlled sort state — drives col.getIsSorted() and the caret icons.
      // Without this, manualSorting:true leaves TanStack with no sort state
      // to read from at all.
      sorting: sortState.field
        ? [{ id: sortState.field, desc: sortState.order === 'desc' }]
        : [],
      columnVisibility: initialColumnVisibility,
    },
  });

  // ── Sort handler — preserves the { sortField, sortOrder } shape setSort expects
  const handleSortClick = (col) => {
    if (!col.getCanSort()) return;

    const isCurrentColumn = sortState.field === col.id;
    const nextOrder = isCurrentColumn && sortState.order === 'asc' ? 'desc' : 'asc';

    setSortState({ field: col.id, order: nextOrder });
    setSort({ sortField: col.id, sortOrder: nextOrder });
    // Reset to page 1 on sort change (mirrors old remote table behaviour)
    setPageNum(1);
  };

  // ── Compact page number window: always show first, last, current ±1
  const pageNumbers = useMemo(() => {
    if (pageCount <= 1) return [];
    const pages = new Set([1, pageCount, pageNum]);
    if (pageNum > 1) pages.add(pageNum - 1);
    if (pageNum < pageCount) pages.add(pageNum + 1);
    return [...pages].sort((a, b) => a - b);
  }, [pageCount, pageNum]);

  const emptyLayout = (
    <Row className="empty-row">
      <Col className="my-auto text-center">
        <h2>No results</h2>
        <p>Please try a different search term.</p>
      </Col>
    </Row>
  );

  return (
    <div id="privoro-table" data-testid={testId}>
      <Row>
        <Col>
          <BTable
            hover
            bordered={false}
            className="parent-table"
            responsive
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sortDir = header.column.getIsSorted();

                    return (
                      <th
                        key={header.id}
                        className={cx(
                          { 'col-sortable': canSort, 'col-sorted': !!sortDir },
                        )}
                        onClick={canSort ? () => handleSortClick(header.column) : undefined}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className="ms-1">
                            {sortDir === 'asc'  && <FontAwesomeIcon icon={faCaretUp} />}
                            {sortDir === 'desc' && <FontAwesomeIcon icon={faCaretDown} />}
                            {!sortDir           && <span>&nbsp;&nbsp;</span>}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody>
              {!loading && data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="p-0 border-0">
                    {emptyLayout}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={cx({
                      'selected-row': !disableRowClick && isShowing && selectedId === resolveField(row.original, columns[0].dataField),
                      'row-clickable': !disableRowClick,
                    })}
                    onClick={
                      disableRowClick
                        ? undefined
                        : () => {
                            setSelectedId(resolveField(row.original, columns[0].dataField));
                            togglePortal(row.original);
                          }
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </BTable>

          {/* ── Pagination footer ─────────────────────────────────────── */}
          {totalRecords > 0 && (
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2 react-bootstrap-table-pagination">

              {/* Left: "Results Per Page / Showing X - Y of Z" — mirrors customTotalRender */}
              <div className="d-flex align-items-center gap-2">
                <span className="me-1 text-nowrap pagination-label">
                  Results Per Page.
                </span>
                <Form.Select
                  className="pagination-page-size"
                  value={tablePageSize}
                  onChange={(e) => {
                    setTablePageSize(Number(e.target.value));
                    setPageNum(1);
                  }}
                  aria-label="Results per page"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Form.Select>
                <span className="react-bootstrap-table-pagination-total text-nowrap pagination-label">
                  Showing {firstOnPage} – {lastOnPage} of {totalRecords} Results
                </span>
              </div>

              {/* Right: page controls */}
              {pageCount > 1 && (
                <Pagination className="mb-0 align-items-center">
                  <Pagination.First
                    onClick={() => setPageNum(1)}
                    disabled={pageNum === 1}
                  />
                  <Pagination.Prev
                    onClick={() => setPageNum(pageNum - 1)}
                    disabled={pageNum === 1}
                  />
                  {pageNumbers.map((page, i) => {
                    const prev = pageNumbers[i - 1];
                    return (
                      <Fragment key={page}>
                        {prev !== undefined && page - prev > 1 && (
                          <Pagination.Ellipsis disabled />
                        )}
                        <Pagination.Item
                          active={page === pageNum}
                          onClick={() => setPageNum(page)}
                        >
                          {page}
                        </Pagination.Item>
                      </Fragment>
                    );
                  })}
                  <Pagination.Next
                    onClick={() => setPageNum(pageNum + 1)}
                    disabled={pageNum === pageCount}
                  />
                  <Pagination.Last
                    onClick={() => setPageNum(pageCount)}
                    disabled={pageNum === pageCount}
                  />
                </Pagination>
              )}
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Table;
